import React from 'react';
import { Control, Controller } from 'react-hook-form';

import { AttorneySelect } from '../matter/AttorneySelect';
import {
  Description,
  ErrorMessage,
  Field,
  Label,
} from '../ui/catalyst/fieldset';
import { Input } from '../ui/catalyst/input';
import { DuplicateSuggestions } from './DuplicateSuggestions';
import { FormSection } from './FormSection';
import { CreateMatterFormValues, UpdateMatterFormValues } from './types';
import { useDuplicateDetection } from './useDuplicateDetection';

interface BasicsSectionProps {
  control: Control<CreateMatterFormValues | UpdateMatterFormValues>;
  errors: any;
  isLoading: boolean;
  hidePhoneNumber?: boolean;
  matterName?: string;
}

export const BasicsSection: React.FC<BasicsSectionProps> = ({
  control,
  errors,
  isLoading,
  hidePhoneNumber = false,
  matterName = '',
}) => {
  const { duplicates, isLoading: isCheckingDuplicates } =
    useDuplicateDetection(matterName);

  return (
    <FormSection
      title="Basics"
      description="Basic information about the matter"
    >
      <div className="space-y-6">
        <Field>
          <Label htmlFor="name">
            Matter Name <span className="text-red-500">*</span>
          </Label>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <Input
                id="name"
                type="text"
                data-slot="control"
                disabled={isLoading}
                {...field}
              />
            )}
          />
          {errors.name?.message && (
            <ErrorMessage>{errors.name.message}</ErrorMessage>
          )}
          {/* Duplicate detection suggestions - shown as subtext */}
          <DuplicateSuggestions
            suggestions={duplicates}
            isLoading={isCheckingDuplicates}
          />
        </Field>

        {!hidePhoneNumber && (
          <Field>
            <Label htmlFor="phoneNumber">Phone Number</Label>
            <Description>
              Primary contact phone number for this matter
            </Description>
            <Controller
              name="phoneNumber"
              control={control}
              render={({ field }) => (
                <Input
                  id="phoneNumber"
                  type="tel"
                  data-slot="control"
                  disabled={isLoading}
                  {...field}
                />
              )}
            />
            {errors.phoneNumber?.message && (
              <ErrorMessage>{errors.phoneNumber.message}</ErrorMessage>
            )}
          </Field>
        )}

        <Field>
          <Label htmlFor="representing_attorney">Representing Attorney</Label>
          <Description>Select an attorney for this matter</Description>
          <Controller
            name="representing_attorney"
            control={control}
            render={({ field }) => (
              <AttorneySelect
                id="representing_attorney"
                firmId={(control._formValues as any)?.firm?.id || ''}
                value={(field.value as string | null) ?? null}
                onChange={(val) => field.onChange(val)}
                placeholder="Select attorney"
              />
            )}
          />
        </Field>
      </div>
    </FormSection>
  );
};
